import get from 'lodash/get';
import moment from 'moment';
import BigNumber from 'bignumber.js';

import type DateRange from '../../lib/dateRange';
import { PeriodSizes } from '../../lib/constants';
import { type Measure, type MeasureErrors, type MetricSeriesResult } from '../../types';
import { UID_REGEX } from 'common/http/constants';
import * as ReportingPeriods from '../../lib/reportingPeriods';

import {
  joinWhereClauses,
  filterWhereClauses,
  setupSoqlDataProvider,
  applyAdditionalFiltersToMeasure,
} from '../helpers';
import type SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';

/**
 * Core batch calculation utilities for performance measures.
 * Shared functionality across all batch calculation types (average, sum, count, etc.)
 */

export interface BatchedResult {
  period: string;
  value: string | null;
}

/**
 * Raw query result row interface for batch calculations
 */
interface QueryResultRow {
  [key: string]: string | number | null;
}

/**
 * Generic query builder result
 */
interface QueryBuilderResult {
  query: string;
  resultAlias: string;
}

/**
 * Creates a date range filter clause for SQL queries
 */
function buildDateRangeFilter(dateColumn: string, startDate: moment.Moment, endDate: moment.Moment): string {
  const startDateStr = startDate.format('YYYY-MM-DDTHH:mm:ss.SSS');
  const endDateStr = endDate.format('YYYY-MM-DDTHH:mm:ss.SSS');
  return `${dateColumn} >= '${startDateStr}' AND ${dateColumn} < '${endDateStr}'`;
}

/**
 * Generic query template builder for batch calculations
 */
function buildQueryTemplate(
  caseExpression: string,
  allWhereClauses: string[],
  aggregationFunction: string,
  resultAlias: string,
): string {
  const periodAlias = '__batch_period_alias__';

  return `
    select
      ${caseExpression} as ${periodAlias},
      ${aggregationFunction} as ${resultAlias}
    where ${joinWhereClauses(allWhereClauses)}
    group by ${caseExpression}
    order by ${periodAlias}
  `.trim();
}

/**
 * Builds a SoQL query for batch calculations using date truncation functions
 */
function buildDateTruncQuery(
  dateTruncFunc: string,
  dateColumn: string,
  allWhereClauses: string[],
  aggregationFunction: string,
  resultAlias: string,
): string {
  const periodAlias = '__batch_period_alias__';
  const whereClause = `where ${joinWhereClauses(allWhereClauses)}`;
  const groupBy = `group by ${dateTruncFunc}(${dateColumn})`;
  const orderBy = `order by ${periodAlias}`;

  return `
    select
      ${dateTruncFunc}(${dateColumn}) as ${periodAlias},
      ${aggregationFunction} as ${resultAlias}
    ${whereClause}
    ${groupBy}
    ${orderBy}
  `.trim();
}

/**
 * Converts period size to SOQL date truncation function for daily and monthly periods
 */
export function getDateTruncFunction(periodSize: PeriodSizes): string {
  switch (periodSize) {
    case PeriodSizes.DAY:
      return 'date_trunc_ymd';
    case PeriodSizes.MONTH:
      return 'date_trunc_ym';
    default:
      throw new Error(`Unsupported period size for date truncation: ${periodSize}`);
  }
}

/**
 * Formats a moment date according to the period size for grouping
 */
export function formatPeriodKey(date: moment.Moment, periodSize: PeriodSizes): string {
  switch (periodSize) {
    case PeriodSizes.DAY:
      return date.format('YYYY-MM-DD');
    case PeriodSizes.WEEK:
      // Use the date directly as it should already be the start of the reporting period
      return date.format('YYYY-MM-DD');
    case PeriodSizes.MONTH:
      return date.format('YYYY-MM');
    case PeriodSizes.QUARTER:
      // Use the date directly as it should already be the start of the reporting period
      return date.format('YYYY-MM-DD');
    case PeriodSizes.YEAR:
      return date.format('YYYY');
    default:
      throw new Error(`Unsupported period size for formatting: ${periodSize}`);
  }
}

/**
 * Converts a reporting period date to a fiscal year key
 */
export function formatFiscalYearKey(date: moment.Moment, fiscalYearStartMonth: number): string {
  let fiscalYear: number;
  if (date.month() >= fiscalYearStartMonth) {
    // Month is after fiscal year start, so it's part of the current fiscal year
    fiscalYear = date.year();
  } else {
    // Month is before fiscal year start, so it's part of the previous fiscal year
    fiscalYear = date.year() - 1;
  }
  return `FY${fiscalYear}`;
}

/**
 * Builds a fiscal year batch query using CASE statement for efficient single-query grouping
 */
function buildFiscalYearQuery(
  column: string,
  dateColumn: string,
  whereClauses: string[],
  reportingPeriods: DateRange[],
  fiscalYearStartMonth: number,
  aggregationFunction: string,
  resultAlias: string,
): QueryBuilderResult {
  // Group reporting periods by fiscal year and collect unique fiscal years
  const fiscalYearRanges = new Map<string, { start: moment.Moment; end: moment.Moment }>();

  reportingPeriods.forEach((period) => {
    const fiscalYearKey = formatFiscalYearKey(period.start, fiscalYearStartMonth);

    if (!fiscalYearRanges.has(fiscalYearKey)) {
      // Calculate fiscal year start and end dates
      const periodDate = period.start;
      let fiscalYearStart: moment.Moment;

      if (periodDate.month() >= fiscalYearStartMonth) {
        // Current fiscal year
        fiscalYearStart = moment([periodDate.year(), fiscalYearStartMonth, 1]);
      } else {
        // Previous fiscal year
        fiscalYearStart = moment([periodDate.year() - 1, fiscalYearStartMonth, 1]);
      }

      const fiscalYearEnd = fiscalYearStart.clone().add(1, 'year');

      fiscalYearRanges.set(fiscalYearKey, {
        start: fiscalYearStart,
        end: fiscalYearEnd,
      });
    }
  });

  // Build CASE statement for fiscal year grouping
  const caseStatements: string[] = [];
  const overallStartDate = moment.min([...fiscalYearRanges.values()].map((r) => r.start));
  const overallEndDate = moment.max([...fiscalYearRanges.values()].map((r) => r.end));

  fiscalYearRanges.forEach((dateRange, fiscalYearKey) => {
    const startDate = dateRange.start.format('YYYY-MM-DDTHH:mm:ss.SSS');
    const endDate = dateRange.end.format('YYYY-MM-DDTHH:mm:ss.SSS');

    caseStatements.push(
      `WHEN ${dateColumn} >= '${startDate}' AND ${dateColumn} < '${endDate}' THEN '${fiscalYearKey}'`,
    );
  });

  const caseExpression = `CASE\n      ${caseStatements.join('\n      ')}\n    END`;

  // Add overall date range filter to optimize query
  const overallDateFilter = buildDateRangeFilter(dateColumn, overallStartDate, overallEndDate);
  const allWhereClauses = [...whereClauses, overallDateFilter];

  const query = buildQueryTemplate(caseExpression, allWhereClauses, aggregationFunction, resultAlias);

  return { query, resultAlias };
}

/**
 * Builds a weekly batch query using CASE statement for efficient single-query grouping
 */
function buildWeeklyQuery(
  column: string,
  dateColumn: string,
  whereClauses: string[],
  reportingPeriods: DateRange[],
  aggregationFunction: string,
  resultAlias: string,
): QueryBuilderResult {
  // Create CASE statements for each weekly period
  const caseStatements: string[] = [];
  const overallStartDate = moment.min(reportingPeriods.map((p) => p.start));
  const overallEndDate = moment.max(reportingPeriods.map((p) => p.end));

  reportingPeriods.forEach((period) => {
    const weekKey = formatPeriodKey(period.start, PeriodSizes.WEEK);
    const startDate = period.start.format('YYYY-MM-DDTHH:mm:ss.SSS');
    const endDate = period.end.format('YYYY-MM-DDTHH:mm:ss.SSS');

    caseStatements.push(
      `WHEN ${dateColumn} >= '${startDate}' AND ${dateColumn} < '${endDate}' THEN '${weekKey}'`,
    );
  });

  const caseExpression = `CASE\n      ${caseStatements.join('\n      ')}\n    END`;

  // Add overall date range filter to optimize query
  const overallDateFilter = buildDateRangeFilter(dateColumn, overallStartDate, overallEndDate);
  const allWhereClauses = [...whereClauses, overallDateFilter];

  const query = buildQueryTemplate(caseExpression, allWhereClauses, aggregationFunction, resultAlias);

  return { query, resultAlias };
}

/**
 * Builds a quarterly batch query using CASE statement for efficient single-query grouping
 */
function buildQuarterlyQuery(
  column: string,
  dateColumn: string,
  whereClauses: string[],
  reportingPeriods: DateRange[],
  aggregationFunction: string,
  resultAlias: string,
): QueryBuilderResult {
  // Create CASE statements for each quarterly period
  const caseStatements: string[] = [];
  const overallStartDate = moment.min(reportingPeriods.map((p) => p.start));
  const overallEndDate = moment.max(reportingPeriods.map((p) => p.end));

  reportingPeriods.forEach((period) => {
    const quarterKey = formatPeriodKey(period.start, PeriodSizes.QUARTER);
    const startDate = period.start.format('YYYY-MM-DDTHH:mm:ss.SSS');
    const endDate = period.end.format('YYYY-MM-DDTHH:mm:ss.SSS');

    caseStatements.push(
      `WHEN ${dateColumn} >= '${startDate}' AND ${dateColumn} < '${endDate}' THEN '${quarterKey}'`,
    );
  });

  const caseExpression = `CASE\n      ${caseStatements.join('\n      ')}\n    END`;

  // Add overall date range filter to optimize query
  const overallDateFilter = buildDateRangeFilter(dateColumn, overallStartDate, overallEndDate);
  const allWhereClauses = [...whereClauses, overallDateFilter];

  const query = buildQueryTemplate(caseExpression, allWhereClauses, aggregationFunction, resultAlias);

  return { query, resultAlias };
}

/**
 * Executes a batched query for multiple reporting periods
 */
export async function executeBatchedQuery(
  dataProvider: SoqlDataProvider,
  column: string,
  dateColumn: string,
  whereClauses: string[],
  reportingPeriods: DateRange[],
  periodSize: PeriodSizes,
  queryType: 'average' | 'sum' | 'count', // Average, sum and count supported
  fiscalYearStartMonth?: number, // For yearly periods with custom fiscal year start
): Promise<BatchedResult[]> {
  if (reportingPeriods.length === 0) {
    return [];
  }

  const periodAlias = '__batch_period_alias__';
  const resultAlias = `__batch_${queryType}_alias__`;

  // Build aggregation function based on query type
  let aggregationFunction: string;
  switch (queryType) {
    case 'average':
      // Use SUM/COUNT to match non-batch behavior where NULLs are treated as 0
      // SUM ignores NULLs, COUNT(*) includes all rows (including NULLs)
      aggregationFunction = `sum(${column}) / count(*)`;
      break;
    case 'sum':
      aggregationFunction = `sum(${column})`;
      break;
    case 'count':
      aggregationFunction = 'count(*)';
      break;
    default:
      throw new Error(`Unsupported query type: ${queryType}`);
  }

  let query: string;

  if (periodSize === PeriodSizes.WEEK) {
    // Use CASE statement for weekly periods - efficient single query
    const result = buildWeeklyQuery(
      column,
      dateColumn,
      whereClauses,
      reportingPeriods,
      aggregationFunction,
      resultAlias,
    );
    query = result.query;
  } else if (periodSize === PeriodSizes.QUARTER) {
    // Use CASE statement for quarterly periods - efficient single query
    const result = buildQuarterlyQuery(
      column,
      dateColumn,
      whereClauses,
      reportingPeriods,
      aggregationFunction,
      resultAlias,
    );
    query = result.query;
  } else if (periodSize === PeriodSizes.YEAR) {
    // Use special fiscal year query with CASE statement for efficient single-query grouping
    const fyStartMonth = fiscalYearStartMonth ?? 0; // Default to January if not provided
    const result = buildFiscalYearQuery(
      column,
      dateColumn,
      whereClauses,
      reportingPeriods,
      fyStartMonth,
      aggregationFunction,
      resultAlias,
    );
    query = result.query;
  } else {
    // For daily and monthly periods
    const dateTruncFunc = getDateTruncFunction(periodSize);
    const firstPeriod = reportingPeriods[0];
    const lastPeriod = reportingPeriods[reportingPeriods.length - 1];
    const batchDateFilter = buildDateRangeFilter(dateColumn, firstPeriod.start, lastPeriod.end);
    const allWhereClauses = [...whereClauses, batchDateFilter];

    query = buildDateTruncQuery(dateTruncFunc, dateColumn, allWhereClauses, aggregationFunction, resultAlias);
  }

  const data = await dataProvider.rawQuery(query);

  return data.map((row: QueryResultRow) => ({
    period: String(row[periodAlias]),
    value: row[resultAlias] !== null ? String(row[resultAlias]) : null,
  }));
}

/**
 * Setup interface for batch calculations
 */
export interface BatchCalculationSetup {
  errors: MeasureErrors;
  measureWithFilters: Measure | null;
  adjustedReportingPeriods: DateRange[];
  column: string;
  valueColumn: string;
  dateColumn: string;
  calculationWhereClause: string[];
  periodSize: PeriodSizes;
  dataProvider: SoqlDataProvider;
  fiscalYearStartMonth?: number;
}

/**
 * Validates measure configuration and sets up common variables for batch calculations
 */
export function setupBatchCalculation(
  measure: Measure | null,
  reportingPeriods: DateRange[],
): BatchCalculationSetup | { errors: MeasureErrors; shouldReturn: true } {
  const errors: MeasureErrors = {};

  // Handle null measure
  if (!measure) {
    errors.calculationNotConfigured = true;
    return { errors, shouldReturn: true };
  }

  const reportingPeriod = measure?.metricConfig?.reportingPeriod;
  errors.noReportingPeriodConfigured =
    !reportingPeriod || !ReportingPeriods.isFullyConfigured(reportingPeriod);
  errors.noReportingPeriodAvailable = reportingPeriods.length === 0;
  errors.dataSourceNotConfigured = !measure?.dataSourceLensUid || !UID_REGEX.test(measure?.dataSourceLensUid);

  // Return early if we have critical errors
  if (
    errors.noReportingPeriodConfigured ||
    errors.noReportingPeriodAvailable ||
    errors.dataSourceNotConfigured
  ) {
    return { errors, shouldReturn: true };
  }

  // Apply additional filters
  const measureWithFilters = applyAdditionalFiltersToMeasure(measure);

  // Apply forCalculation transformation to each period
  const adjustedReportingPeriods = reportingPeriods
    .map((period) => period.forCalculation(measureWithFilters))
    .filter(Boolean) as DateRange[];

  const metricConfigType = get(measureWithFilters, 'metricConfig.type');
  const column = get(measureWithFilters, 'metricConfig.arguments.column', '');
  const valueColumn = get(measureWithFilters, 'metricConfig.arguments.valueColumn', '');
  const dateColumn = get(measureWithFilters, 'metricConfig.dateColumn');
  const calculationFilters = get(measureWithFilters, 'metricConfig.arguments.calculationFilters', []);
  const calculationWhereClause = filterWhereClauses(calculationFilters);
  const periodSize = get(measureWithFilters, 'metricConfig.reportingPeriod.size');

  const dataProvider = setupSoqlDataProvider(measureWithFilters);

  // Validate required fields based on calculation type
  let hasRequiredColumn = false;
  if (metricConfigType === 'count' || metricConfigType === 'rate') {
    hasRequiredColumn = true; // count and rate don't require column/valueColumn
  } else if (metricConfigType === 'recent') {
    hasRequiredColumn = !!valueColumn; // recent requires valueColumn
  } else {
    hasRequiredColumn = !!column; // sum, average require column
  }

  if (!hasRequiredColumn || !dateColumn || !dataProvider || !periodSize) {
    errors.calculationNotConfigured = true;
    return { errors, shouldReturn: true };
  }

  // Get fiscal year start month for yearly periods
  let fiscalYearStartMonth: number | undefined;
  if (periodSize === PeriodSizes.YEAR) {
    if (measureWithFilters?.metricConfig?.reportingPeriod?.startDateConfig?.date) {
      fiscalYearStartMonth = moment(
        measureWithFilters.metricConfig.reportingPeriod.startDateConfig.date,
      ).month();
    } else {
      fiscalYearStartMonth = 0;
    }
  } else {
    fiscalYearStartMonth = undefined;
  }

  return {
    errors,
    measureWithFilters,
    adjustedReportingPeriods,
    column,
    valueColumn,
    dateColumn,
    calculationWhereClause,
    periodSize,
    dataProvider,
    fiscalYearStartMonth,
  };
}

/**
 * Processes batch query results and maps them back to reporting periods
 */
export function processBatchResults(
  batchData: BatchedResult[],
  adjustedReportingPeriods: DateRange[],
  periodSize: PeriodSizes,
  fiscalYearStartMonth: number | undefined,
  errors: MeasureErrors,
  queryType?: 'average' | 'sum' | 'count',
): Omit<MetricSeriesResult, 'date'>[] {
  // Process results based on period size
  const processedData = new Map<string, { value: BigNumber }>();

  // All period types use direct results with CASE statement optimization
  // Weekly, quarterly, and yearly periods are handled efficiently by the database
  batchData.forEach((row) => {
    if (row.value === null) {
      return; // Skip NULL results - they represent periods with no data
    }
    const value = new BigNumber(row.value);

    // For weekly, quarterly, and yearly periods, the period key is already formatted correctly
    // from the CASE statement (e.g., 'FY2024', '2024-01-01', '2024-01-01')
    // For daily and monthly periods, format the period key to match formatPeriodKey output
    let formattedKey: string;
    if (
      periodSize === PeriodSizes.WEEK ||
      periodSize === PeriodSizes.QUARTER ||
      periodSize === PeriodSizes.YEAR
    ) {
      formattedKey = row.period; // Already correctly formatted from CASE statement
    } else {
      const periodDate = moment(row.period);
      formattedKey = formatPeriodKey(periodDate, periodSize);
    }
    processedData.set(formattedKey, { value });
  });

  // Map results back to reporting periods
  return adjustedReportingPeriods.map((period) => {
    let periodKey: string;
    if (periodSize === PeriodSizes.YEAR) {
      // For fiscal years, use the fiscal year key format
      if (fiscalYearStartMonth === undefined) {
        // Return error result if fiscalYearStartMonth is not provided for yearly periods
        return {
          errors: {
            ...errors,
            calculationNotConfigured: true,
          },
          result: { value: null },
        };
      }
      periodKey = formatFiscalYearKey(period.start, fiscalYearStartMonth);
    } else {
      periodKey = formatPeriodKey(period.start, periodSize);
    }
    const data = processedData.get(periodKey);

    // A period with no matching rows produces no group in the SQL result set at all
    // (GROUP BY never emits empty groups), so `data` is undefined here. For count,
    // that means the true count is 0, not "not enough data" - matching the non-batch
    // count calculation's `count(*) || 0` behavior in ../count.js.
    if (!data && queryType === 'count') {
      return {
        errors: { ...errors },
        result: { value: new BigNumber(0) },
      };
    }

    if (!data || data.value.isNaN()) {
      return {
        errors: {
          ...errors,
          notEnoughData: true,
        },
        result: { value: null },
      };
    }

    return {
      errors: {
        ...errors,
      },
      result: { value: data.value },
    };
  });
}
